import {
  ConfirmationModal,
  ErrorMessage,
  ObjectPillListItem,
  TargetAudienceSelectorPopover,
  Toggle,
  useAuthUser,
  ObjectPillList,
  Button,
  removeFollowingFields,
  pickAllowedFields,
  deepOmit,
  SlideShow,
  GET_ALL_FORM_VERSIONS_FOR_CURRENT_USER,
  GET_ALL_FORM_TEMPLATES_FOR_CURRENT_USER_IF_HAS_VERSION,
  GET_EMPLOYEES_FOR_CURRENT_COMPANY,
  UPDATE_FORM,
  ADD_FORM,
  GET_TARGET_AUDIENCE_COUNTS,
  useCurrentCompany,
} from '@employ-engage/common-components';
import { useFormik, getIn } from 'formik';
import {
  Company,
  TargetAudienceTypeEnum,
  FormType,
  Employee,
  FormVersion,
  FormTemplateWithMostRecentVersion,
  FormTemplate,
  ManagementRole,
  departmentStringToFriendlyString,
  Target,
  TargetWithCompleteEmployees,
} from '@employ-engage/data';
import { addWeeks, format } from 'date-fns';
import * as Yup from 'yup';
import { useState, ReactNode, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import TargetReach from './target-reach';

/**
 * A saved form opened for editing: its targets hold the employees themselves
 * rather than the ids stored on the form, so they can be shown and changed.
 */
export type FormWithCompleteTargets = Omit<FormType, 'targets'> & {
  targets: EditableTarget[];
};

/** `sendEmail` is not saved with the form; it only drives the send on submit. */
type EditableTarget = TargetWithCompleteEmployees & { sendEmail?: boolean };

export interface AddFormFormProps {
  formSelectedForEditing: FormWithCompleteTargets | null;
  setOpen: (open: boolean) => void;
  refetchForms?: () => void;
}

/** Where each of a target's three audiences keeps its selections. */
const AUDIENCE_LABELS = {
  invite: {
    targetIndividualsLabel: 'targetIndividuals',
    targetDepartmentsLabel: 'targetDepartments',
    targetManagementRolesLabel: 'targetManagementRoles',
  },
  responses: {
    targetIndividualsLabel: 'targetIndividualsForResponses',
    targetDepartmentsLabel: 'targetDepartmentsForResponses',
    targetManagementRolesLabel: 'targetManagementRolesForResponses',
  },
  textResponses: {
    targetIndividualsLabel: 'targetIndividualsForTextResponses',
    targetDepartmentsLabel: 'targetDepartmentsForTextResponses',
    targetManagementRolesLabel: 'targetManagementRolesForTextResponses',
  },
};

/**
 * One audience as a `TargetInput`. The count and list queries read the invite
 * fields, so a response audience is moved into them; the server's matching
 * rule is the same for all three.
 */
const toAudienceTarget = (
  target: any,
  labels: (typeof AUDIENCE_LABELS)[keyof typeof AUDIENCE_LABELS]
) => ({
  targetDepartments: target[labels.targetDepartmentsLabel] || [],
  targetManagementRoles: (target[labels.targetManagementRolesLabel] || []).map(
    (role: ManagementRole) => ({ id: role.id, name: role.name })
  ),
  targetIndividuals: (target[labels.targetIndividualsLabel] || []).map(
    (individual: Employee) => individual.id
  ),
  shouldSendConfirmationEmailToFormSubmitter: false,
  shouldSendConfirmationSmsToFormSubmitter: false,
});

const namesAnyone = (target: ReturnType<typeof toAudienceTarget>) =>
  target.targetDepartments.length > 0 ||
  target.targetManagementRoles.length > 0 ||
  target.targetIndividuals.length > 0;

export function AddFormForm({
  formSelectedForEditing,
  setOpen,
  refetchForms,
}: AddFormFormProps) {
  const [titleInputRef, setTitleInputRef] = useState<HTMLInputElement | null>(
    null
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  /**
   * The form this dialog created, once it has. Saving again then updates it
   * rather than creating a second one — which is what makes it safe to keep
   * the dialog open when the emails after a first save fail.
   */
  const [savedForm, setSavedForm] = useState<FormType | null>(null);
  const editingForm = formSelectedForEditing ?? savedForm;
  const [isConfirmingSend, setIsConfirmingSend] = useState(false);
  const { t } = useTranslation(['common']);
  const { data: { employees } = [] } = useQuery(
    GET_EMPLOYEES_FOR_CURRENT_COMPANY
  );

  const { data: { formVersions } = { formVersions: [] } } = useQuery(
    GET_ALL_FORM_VERSIONS_FOR_CURRENT_USER
  );
  const {
    data: {
      getFormTemplatesForCurrentUserOnlyIfTheyHaveVersions: formTemplates,
    } = {},
    refetch: refetchFormTemplates,
  } = useQuery(GET_ALL_FORM_TEMPLATES_FOR_CURRENT_USER_IF_HAS_VERSION);
  const [updateForm] = useMutation(UPDATE_FORM);
  const [addForm] = useMutation(ADD_FORM);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { user } = useAuthUser();
  const { currentCompany: companyData, refetchCompany } = useCurrentCompany();
  const [templateId, setTemplateId] = useState<string>(
    formSelectedForEditing
      ? getTemplateIdFromTemplateVersionId(
          formSelectedForEditing.templateVersionId
        ) || ''
      : ''
  );

  const now = new Date();

  function getTemplateIdFromTemplateVersionId(templateVersionId: string) {
    const templateVersion = formVersions.find(
      (version: FormVersion) => version.id === templateVersionId
    );
    return templateVersion?.templateId;
  }

  useEffect(() => {
    if (formSelectedForEditing) {
      refetchFormTemplates().then(() => {
        const templateId = getTemplateIdFromTemplateVersionId(
          formSelectedForEditing.templateVersionId
        );
        setTemplateId(templateId || '');
      });
    }
  }, [formSelectedForEditing, refetchFormTemplates]);

  const createNewTarget = (): EditableTarget => ({
    sendEmail: false, // Not saved in the database. Only for sending an email onSubmit
    targetIndividuals: [] as Employee[],
    targetIndividualsForResponses: [] as Employee[],
    targetIndividualsForTextResponses: [] as Employee[],
    targetDepartments: [],
    targetDepartmentsForResponses: [],
    targetDepartmentsForTextResponses: [],
    targetManagementRoles: [],
    targetManagementRolesForResponses: [],
    targetManagementRolesForTextResponses: [],
    addResponseEmailRecipients: false,
    addResponseTextRecipients: false,
    addTargetManagementRoles: false,
    shouldSendConfirmationEmailToFormSubmitter: false,
    shouldSendConfirmationSmsToFormSubmitter: false,
  });

  /**
   * `.required()` on an array accepts `[]` — it only rejects null and
   * undefined. So the "at least one selection is required" the picker
   * advertised was never enforced. The rule is now a real one, and the same
   * one the server applies: a level, a role or a person. A role alone reaches
   * everyone who holds it.
   */
  const targetSchema = Yup.object().shape({
    targetDepartments: Yup.array().of(Yup.string()).required(),
    targetDepartmentsForResponses: Yup.array().of(Yup.string()).required(),
    targetDepartmentsForTextResponses: Yup.array().of(Yup.string()).required(),
    targetManagementRoles: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string().required(),
          name: Yup.string().required(),
        })
      )
      .required(),
    targetManagementRolesForResponses: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string().required(),
          name: Yup.string().required(),
        })
      )
      .required(),
    targetManagementRolesForTextResponses: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string().required(),
          name: Yup.string().required(),
        })
      )
      .required(),
    targetIndividuals: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string().required(),
          firstName: Yup.string().required(),
          lastName: Yup.string().required(),
          emailAddress: Yup.string().email().required(),
          phoneNumber: Yup.string().nullable(),
          department: Yup.array().of(Yup.string()).required(),
          managementRoles: Yup.array()
            .of(
              Yup.object().shape({
                id: Yup.string().required(),
                name: Yup.string().required(),
              })
            )
            .required(),
        })
      )
      .required(),
    targetIndividualsForResponses: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string().required(),
          firstName: Yup.string().required(),
          lastName: Yup.string().required(),
          emailAddress: Yup.string().email().required(),
          phoneNumber: Yup.string().nullable(),
          department: Yup.array().of(Yup.string()).required(),
          managementRoles: Yup.array()
            .of(
              Yup.object().shape({
                id: Yup.string().required(),
                name: Yup.string().required(),
              })
            )
            .required(),
        })
      )
      .required(),
    targetIndividualsForTextResponses: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string().required(),
          firstName: Yup.string().required(),
          lastName: Yup.string().required(),
          emailAddress: Yup.string().email().required(),
          phoneNumber: Yup.string().nullable(),
          department: Yup.array().of(Yup.string()).required(),
          managementRoles: Yup.array()
            .of(
              Yup.object().shape({
                id: Yup.string().required(),
                name: Yup.string().required(),
              })
            )
            .required(),
        })
      )
      .required(),
    addResponseEmailRecipients: Yup.boolean(),
    addResponseTextRecipients: Yup.boolean(),
    addTargetManagementRoles: Yup.boolean(),
    shouldSendConfirmationEmailToFormSubmitter: Yup.boolean(),
    shouldSendConfirmationSmsToFormSubmitter: Yup.boolean(),
    sendEmail: Yup.boolean(),
  }).test(
    'targets-at-least-one-person',
    t('Select at least one organization level, role or individual.'),
    (target: any) =>
      (target?.targetDepartments?.length ?? 0) > 0 ||
      (target?.targetManagementRoles?.length ?? 0) > 0 ||
      (target?.targetIndividuals?.length ?? 0) > 0
  );

  const {
    handleChange,
    handleBlur,
    values,
    errors,
    touched,
    resetForm,
    setFieldValue,
    setFieldError,
    submitForm,
    validateForm,
  } = useFormik({
    initialValues: formSelectedForEditing
      ? {
          ...formSelectedForEditing,
          templateVersionId: formSelectedForEditing.templateVersionId || '',
          openDate: format(
            formSelectedForEditing.openDate,
            "yyyy-MM-dd'T'HH:mm:ss"
          ),
          closeDate: format(
            formSelectedForEditing.closeDate,
            "yyyy-MM-dd'T'HH:mm:ss"
          ),
          targets: formSelectedForEditing.targets,
        }
      : {
          title: '',
          templateVersionId: '',
          templateId: '',
          targets: [createNewTarget()],
          openDate: format(now, "yyyy-MM-dd'T'HH:mm:ss"),
          closeDate: format(addWeeks(now, 2), "yyyy-MM-dd'T'HH:mm:ss"),
          isAnonymous: false,
          isPasscodeRequired: false,
          isArchived: false,
          ownerId: user?.id || '',
        },
    onSubmit: async (values) => {
      // Anything thrown in here used to vanish: no mutation, no message, and
      // a Save that looked like it worked.
      setSubmitError(null);
      try {
      const templateVersionId = values.templateVersionId || '';
      const openDateUtc = new Date(values.openDate).toISOString();
      const closeDateUtc = new Date(values.closeDate).toISOString();

      const newSurvey = {
        ...values,
        id: editingForm?.id || '',
        templateVersionId,
        targets: [
          ...values.targets.map((target) => {
            const targetIndividualIds = target.targetIndividuals.map(
              (individual: Employee) => individual.id
            );
            return {
              ...target,
              targetIndividuals: targetIndividualIds,
              targetIndividualsForResponses:
                target.targetIndividualsForResponses.map(
                  (individual: Employee) => individual.id
                ),
              targetIndividualsForTextResponses:
                target.targetIndividualsForTextResponses.map(
                  (individual: Employee) => individual.id
                ),
            };
          }),
        ],
        openDate: openDateUtc,
        closeDate: closeDateUtc,
        ownerId: user?.id,
      };
      let form: FormType;
      if (editingForm) {
        const allowedFields = [
          'title',
          'openDate',
          'closeDate',
          'templateVersionId',
          'targets',
          'isAnonymous',
          'isPasscodeRequired',
          'isArchived',
          'ownerId',
          'companyId',
          'createdAt',
          'updatedAt',
        ];

        const allowedTargetFields = [
          'targetDepartments',
          'targetDepartmentsForResponses',
          'targetDepartmentsForTextResponses',
          'targetManagementRoles',
          'targetManagementRolesForResponses',
          'targetManagementRolesForTextResponses',
          'targetIndividuals',
          'targetIndividualsForResponses',
          'targetIndividualsForTextResponses',
          'addResponseEmailRecipients',
          'addResponseTextRecipients',
          'shouldSendConfirmationEmailToFormSubmitter',
          'shouldSendConfirmationSmsToFormSubmitter',
          'addTargetManagementRoles',
        ];

        // When cleaning the survey before update:
        const cleanedSurvey = deepOmit(
          {
            ...pickAllowedFields(newSurvey, allowedFields),
            targets: newSurvey.targets?.map((target: any) => ({
              ...pickAllowedFields(target, allowedTargetFields),
              targetManagementRoles: target.targetManagementRoles,
              targetManagementRolesForResponses:
                target.targetManagementRolesForResponses,
              targetManagementRolesForTextResponses:
                target.targetManagementRolesForTextResponses,
            })),
          },
          ['__typename']
        );

        const { data } = await updateForm({
          variables: {
            id: editingForm.id,
            input: cleanedSurvey,
          },
        });
        form = data?.updateForm;
      } else {
        delete (newSurvey as any).id;
        const input = removeFollowingFields(newSurvey, [
          'templateId',
          'sendEmail',
          '__typename',
        ]);
        const { data } = await addForm({
          variables: {
            input,
          },
        });
        form = data?.createForm;
        setSavedForm(form);
      }

      const outcome = await sendFillOutEmailsIfNeeded(form, values);
      if (outcome === 'empty') {
        refetchForms?.();
        setSubmitError(
          t(
            'Saved, but no emails were sent: everyone this form reaches has already responded.'
          )
        );
        return;
      }
      if (outcome === 'failed') {
        refetchForms?.();
        setSubmitError(
          t('Saved, but the emails could not be sent. Save again to retry.')
        );
        return;
      }
      closeDialog();
      } catch (error: any) {
        console.error('Error saving form:', error);
        setSubmitError(error?.message || t("Couldn't save the form."));
      }
    },
    validationSchema: Yup.object().shape({
      title: Yup.string().required(t('Required')),
      templateVersionId: Yup.string().required(t('Required')),
      targets: Yup.array()
        .of(targetSchema)
        .min(1, t('At least one target is required')),
      openDate: Yup.string().required(t('Required')),
      closeDate: Yup.string().required(t('Required')),
    }),
  });

  /**
   * Who each target reaches right now, counted on the server by the same rule
   * the send-out uses — so a target that matches nobody shows up while it is
   * being built, not as an email that silently never went out.
   */
  const audienceTargets = useMemo(
    () =>
      values.targets.map((target: any) =>
        toAudienceTarget(target, AUDIENCE_LABELS.invite)
      ),
    [values.targets]
  );
  const emailTargetIndexes = values.targets.flatMap((target: any, index) =>
    target.sendEmail ? [index] : []
  );
  const { data: audienceData } = useQuery(GET_TARGET_AUDIENCE_COUNTS, {
    variables: {
      targets: audienceTargets,
      emailTargetIndexes,
      formId: editingForm?.id ?? null,
    },
    skip: audienceTargets.length === 0,
  });
  const audienceCounts:
    | {
        perTarget: number[];
        unreachablePerTarget: number[];
        total: number;
        emailTotal: number;
        emailUnreachable: number;
      }
    | undefined = audienceData?.targetAudienceCounts;

  // Who gets the email and text copies of each response. Counted separately:
  // these never decide who is invited, so they stay out of the totals above.
  const responseTargets = useMemo(
    () =>
      values.targets.map((target: any) =>
        toAudienceTarget(target, AUDIENCE_LABELS.responses)
      ),
    [values.targets]
  );
  const textResponseTargets = useMemo(
    () =>
      values.targets.map((target: any) =>
        toAudienceTarget(target, AUDIENCE_LABELS.textResponses)
      ),
    [values.targets]
  );
  const { data: responseData } = useQuery(GET_TARGET_AUDIENCE_COUNTS, {
    variables: { targets: responseTargets, emailTargetIndexes: [], formId: null },
    skip: !responseTargets.some(namesAnyone),
  });
  const { data: textResponseData } = useQuery(GET_TARGET_AUDIENCE_COUNTS, {
    variables: {
      targets: textResponseTargets,
      emailTargetIndexes: [],
      formId: null,
    },
    skip: !textResponseTargets.some(namesAnyone),
  });
  const responseCounts = responseData?.targetAudienceCounts;
  const textResponseCounts = textResponseData?.targetAudienceCounts;

  /** The "names nobody" message, which Yup puts on the target object itself. */
  const targetLevelError = (index: number): string | undefined => {
    const error = getIn(errors, `targets[${index}]`);
    return typeof error === 'string' ? error : undefined;
  };

  const targetNamesAnyone = (index: number) =>
    namesAnyone(audienceTargets[index]);

  /**
   * Blocks Save when the form would reach nobody at all, or when a target is
   * set to email people and matches none of them. Left to validation when a
   * target names nobody yet, which already says so.
   */
  const audienceProblem: string | null = (() => {
    if (!audienceCounts) return null;
    if (!values.targets.every((_, index) => targetNamesAnyone(index))) {
      return null;
    }
    if (audienceCounts.total === 0) {
      return t(
        'No one would receive this form: no active employee matches any of its targets.'
      );
    }
    const silentIndex = values.targets.findIndex(
      (target: any, index) =>
        target.sendEmail && audienceCounts.perTarget[index] === 0
    );
    if (silentIndex !== -1) {
      return t(
        'Target {{number}} is set to send email but reaches nobody.',
        { number: silentIndex + 1 }
      );
    }
    return null;
  })();

  const closeDialog = () => {
    resetForm();
    setSavedForm(null);
    setOpen(false);
    refetchForms?.();
    refetchCompany();
  };

  /**
   * Sends the invitations for the targets with Send Email on, and says what
   * happened rather than closing the dialog either way: an empty or failed
   * send used to look exactly like a successful one.
   *
   * A declaration, below useFormik on purpose: useFormik's onSubmit calls it.
   */
  async function sendFillOutEmailsIfNeeded(
    form: FormType,
    values: any
  ): Promise<'none' | 'sent' | 'empty' | 'failed'> {
    const sendEmailsForTargets: boolean[] = values.targets.map(
      (target: Target) => !!target.sendEmail
    );
    if (!sendEmailsForTargets.includes(true)) {
      return 'none';
    }

    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_API_ENDPOINT
        }/protected-api/enqueue-emails?type=fill-out-form`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            formId: form?.id,
            sendEmailsForTargets,
          }),
        }
      );
      if (!response.ok) {
        console.error('Failed to send emails', response.status);
        return 'failed';
      }
      const { jobIds = [] } = await response.json().catch(() => ({}));
      return jobIds.length === 0 ? 'empty' : 'sent';
    } catch (error) {
      console.error('Failed to send emails', error);
      return 'failed';
    }
  }

  const isSelectedTemplatePasscodeRequired =
    formTemplates?.find((template: FormTemplate) => {
      return template.id === templateId;
    })?.isPasscodeRequired || false;

  interface PopoverButtonContentsProps {
    index: number;
    targetIndividuals: Employee[];
    targetDepartments: string[];
    targetManagementRoles: ManagementRole[];
    targetIndividualsLabel: string;
    targetDepartmentsLabel: string;
    targetManagementRolesLabel: string;
  }

  /**
   * The pills summarising one audience of one target.
   *
   * Removing a pill used to write to a top-level field — `targetDepartments`
   * rather than `targets[0].targetDepartments` — so it removed nothing from
   * the target, and planted a field the create mutation then rejected. It
   * also matched organization levels by display name against their ids, which
   * never matched. Errors were read from the same wrong place, so none ever
   * showed.
   */
  const PopoverButtonContents = ({
    index,
    targetIndividuals,
    targetDepartments,
    targetManagementRoles,
    targetIndividualsLabel,
    targetDepartmentsLabel,
    targetManagementRolesLabel,
  }: PopoverButtonContentsProps) => {
    const path = (label: string) => `targets[${index}].${label}`;
    const fieldError = [
      targetDepartmentsLabel,
      targetIndividualsLabel,
      targetManagementRolesLabel,
    ]
      .map((label) => getIn(errors, path(label)))
      .find((error) => typeof error === 'string');
    const fieldTouched = [
      targetDepartmentsLabel,
      targetIndividualsLabel,
      targetManagementRolesLabel,
    ].some((label) => !!getIn(touched, path(label)));

    return (
      <div className="w-full">
        <ObjectPillList
          leadingIcon={<i className="fa-solid fa-plus" />}
          className="cursor-pointer"
          list={[
            ...targetIndividuals.map((individual: Employee) => {
              return {
                id: individual.id,
                label: `${individual.employeeId} ${individual.firstName} ${individual.lastName} (${individual.emailAddress})`,
                type: TargetAudienceTypeEnum.INDIVIDUAL,
              };
            }),
            ...targetDepartments.map((department) => {
              return {
                id: department,
                label: departmentStringToFriendlyString(
                  companyData,
                  department
                ),
                type: TargetAudienceTypeEnum.ORGANIZATION,
              };
            }),
            ...targetManagementRoles.map((role) => {
              return {
                id: role.id,
                label: role.name,
                type: TargetAudienceTypeEnum.ROLE,
              };
            }),
          ]}
          onClickXMark={(
            list: ObjectPillListItem[],
            item: ObjectPillListItem
          ) => {
            switch (item.type) {
              case TargetAudienceTypeEnum.ROLE:
                setFieldValue(
                  path(targetManagementRolesLabel),
                  targetManagementRoles?.filter(
                    (role: ManagementRole) => role.id !== item.id
                  )
                );
                break;
              case TargetAudienceTypeEnum.INDIVIDUAL:
                setFieldValue(
                  path(targetIndividualsLabel),
                  targetIndividuals?.filter(
                    (individual: Employee) => individual.id !== item.id
                  )
                );
                break;
              case TargetAudienceTypeEnum.ORGANIZATION:
                setFieldValue(
                  path(targetDepartmentsLabel),
                  targetDepartments?.filter(
                    (department: string) => department !== item.id
                  )
                );
                break;
              default:
                break;
            }
          }}
        />
        <ErrorMessage errors={fieldError} touched={fieldTouched} />
      </div>
    );
  };

  interface TargetAudienceSelectorPopoversProps {
    targetIndividualsLabel: string;
    targetDepartmentsLabel: string;
    targetManagementRolesLabel: string;
  }

  const getTargetAudienceSelectorPopovers = (
    propsArray: TargetAudienceSelectorPopoversProps[],
    index: number
  ) => {
    const targetAudienceSelectorPopovers: ReactNode[] = [];

    propsArray.forEach(
      ({
        targetIndividualsLabel,
        targetDepartmentsLabel,
        targetManagementRolesLabel,
      }) => {
        targetAudienceSelectorPopovers.push(
          <TargetAudienceSelectorPopover
            popoverButtonContents={
              <PopoverButtonContents
                index={index}
                targetIndividuals={
                  (values.targets[index][
                    targetIndividualsLabel as keyof EditableTarget
                  ] as Employee[]) || []
                }
                targetDepartments={
                  (values.targets[index][
                    targetDepartmentsLabel as keyof EditableTarget
                  ] as string[]) || []
                }
                targetManagementRoles={
                  (values.targets[index][
                    targetManagementRolesLabel as keyof EditableTarget
                  ] as ManagementRole[]) || []
                }
                targetIndividualsLabel={targetIndividualsLabel}
                targetDepartmentsLabel={targetDepartmentsLabel}
                targetManagementRolesLabel={targetManagementRolesLabel}
              />
            }
            individuals={employees}
            targetIndividuals={
              (values.targets[index][
                targetIndividualsLabel as keyof EditableTarget
              ] as Employee[]) || []
            }
            onAddTargetIndividual={(list, individual) => {
              const employee = employees.find((employee: Employee) => {
                return employee.id === (individual.key || individual.value);
              });
              const existingItem = (
                values.targets[index][
                  targetIndividualsLabel as keyof EditableTarget
                ] as Employee[]
              ).find((item: Employee) => {
                return item.id === employee.id;
              });
              if (!existingItem) {
                setFieldValue(`targets[${index}].${targetIndividualsLabel}`, [
                  ...((values.targets[index][
                    targetIndividualsLabel as keyof EditableTarget
                  ] as Employee[]) || []),
                  employee,
                ]);
              }
            }}
            onDeleteTargetIndividual={(list, item) => {
              setFieldValue(`targets[${index}].${targetIndividualsLabel}`, [
                ...(
                  (values.targets[index][
                    targetIndividualsLabel as keyof EditableTarget
                  ] as Employee[]) || []
                ).filter((individual) => {
                  return individual.id !== item?.value;
                }),
              ]);
            }}
            companyData={companyData as Company}
            managementRoles={companyData?.managementRoles || []}
            targetManagementRoles={
              (values.targets[index][
                targetManagementRolesLabel as keyof EditableTarget
              ] as ManagementRole[]) || []
            }
            onDeleteTargetManagementRole={(
              list: ManagementRole[],
              item: ManagementRole
            ) => {
              setFieldValue(`targets[${index}].${targetManagementRolesLabel}`, [
                ...(
                  (values.targets[index][
                    targetManagementRolesLabel as keyof EditableTarget
                  ] as ManagementRole[]) || []
                ).filter((role) => {
                  return role.id !== item?.id;
                }),
              ]);
            }}
            onAddTargetManagementRole={(item: ManagementRole) => {
              if (
                !(
                  values.targets[index][
                    targetManagementRolesLabel as keyof EditableTarget
                  ] as ManagementRole[]
                ).includes(item)
              ) {
                setFieldValue(
                  `targets[${index}].${targetManagementRolesLabel}`,
                  [
                    ...((values.targets[index][
                      targetManagementRolesLabel as keyof EditableTarget
                    ] as ManagementRole[]) || []),
                    item,
                  ]
                );
              }
            }}
            targetDepartments={
              (values.targets[index][
                targetDepartmentsLabel as keyof EditableTarget
              ] as string[]) || []
            }
            onDeleteTargetDepartment={(list: string[], item: string) => {
              setFieldValue(`targets[${index}].${targetDepartmentsLabel}`, [
                ...((
                  values.targets[index][
                    targetDepartmentsLabel as keyof EditableTarget
                  ] as string[]
                )?.filter((department) => {
                  return department !== item;
                }) || []),
              ]);
            }}
            onAddTargetDepartment={(targetDepartment: string) => {
              if (
                !(
                  values.targets[index][
                    targetDepartmentsLabel as keyof EditableTarget
                  ] as string[]
                ).includes(targetDepartment)
              ) {
                setFieldValue(`targets[${index}].${targetDepartmentsLabel}`, [
                  ...((values.targets[index][
                    targetDepartmentsLabel as keyof EditableTarget
                  ] as string[]) || []),
                  targetDepartment,
                ]);
              }
            }}
            // The invite audience is the one that must name someone; its error
            // sits on the target itself, not on any one field.
            targetDepartmentErrors={
              targetIndividualsLabel === 'targetIndividuals'
                ? targetLevelError(index)
                : undefined
            }
            targetDepartmentTouched
            onCancel={() => {
              // do nothing
            }}
            targetManagementRolesTestId={`targetManagementRoles-${index}`}
            targetIndividualsTestId={`targetIndividuals-${index}`}
            targetDepartmentsTestId={`targetDepartments-${index}`}
            refetchCompany={refetchCompany}
          />
        );
      }
    );

    return targetAudienceSelectorPopovers;
  };

  const handleLastSlideRightArrowClick = () => {
    setFieldValue('targets', [...values.targets, createNewTarget()]);
    setCurrentSlide(values.targets.length);
  };

  const handleDeleteSlide = (index: number) => {
    setFieldValue('targets', [
      ...values.targets.slice(0, index),
      ...values.targets.slice(index + 1),
    ]);
    setCurrentSlide(index === 0 ? 0 : index - 1);
  };

  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        // Enter in a field submits past the disabled button.
        if (audienceProblem) {
          return;
        }
        // An email cannot be unsent, so a save that sends asks first, with the
        // number. Only once the form is valid: otherwise submit, which marks
        // the fields and shows why.
        if (emailTargetIndexes.length > 0) {
          const validationErrors = await validateForm();
          if (Object.keys(validationErrors).length === 0) {
            setIsConfirmingSend(true);
            return;
          }
        }
        submitForm();
      }}
    >
      <ConfirmationModal
        open={isConfirmingSend}
        setOpen={setIsConfirmingSend}
        text={
          audienceCounts
            ? t('Saving will email {{count}} people now. Send?', {
                count: audienceCounts.emailTotal,
              })
            : t('Saving will send emails now. Send?')
        }
        warningText={
          audienceCounts?.emailUnreachable
            ? t(
                '{{count}} of them have an address that is missing, invalid, a test address, or has bounced before.',
                { count: audienceCounts.emailUnreachable }
              )
            : undefined
        }
        confirmButtonText={t('Save and send')}
        confirmButtonColorClasses="bg-indigo-600 focus:ring-indigo-500 hover:bg-indigo-700"
        onConfirm={() => {
          submitForm();
        }}
      />
      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-1">
          <label
            htmlFor="templateVersionId"
            className="block text-sm font-medium text-gray-700"
          >
            {t('Latest Version of Published Template')}
          </label>
          <select
            id="templateVersionId"
            name="templateVersionId"
            className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            onChange={(e) => {
              const version = formTemplates?.find(
                (template: FormTemplateWithMostRecentVersion) =>
                  template.id === e.target.value
              )?.mostRecentVersion;
              setTemplateId(e.target.value);
              setFieldValue('templateVersionId', version?.id || '');
              setFieldValue('title', version?.name || '');
              titleInputRef?.focus();
              setFieldError('templateVersionId', undefined);
            }}
            onBlur={handleBlur}
            value={templateId}
            disabled={!!formSelectedForEditing}
          >
            <option value="">Select Template</option>
            {formTemplates?.map?.(
              (template: FormTemplateWithMostRecentVersion) => {
                return (
                  <option key={template.id} value={template.id}>
                    {template.mostRecentVersion?.name}
                  </option>
                );
              }
            )}
          </select>
          {errors.templateVersionId && touched.templateVersionId && (
            <ErrorMessage
              errors={errors.templateVersionId}
              touched={touched.templateVersionId}
            />
          )}
        </div>
        <div className="col-span-1">
          <label
            htmlFor="title"
            className="block text-sm font-medium text-gray-700"
          >
            {t('Title')}
          </label>
          <input
            ref={setTitleInputRef}
            type="text"
            name="title"
            id="title"
            className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.title}
          />
          {errors.title && touched.title && (
            <ErrorMessage errors={errors.title} touched={touched.title} />
          )}
        </div>
        <div className="col-span-1">
          <label
            htmlFor="openDate"
            className="block text-sm font-medium text-gray-700"
          >
            {t('Open Date')}
          </label>
          <input
            type="datetime-local"
            name="openDate"
            id="openDate"
            className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.openDate}
          />
          <ErrorMessage errors={errors.openDate} touched={touched.openDate} />
        </div>
        <div className="col-span-1">
          <label
            htmlFor="closeDate"
            className="block text-sm font-medium text-gray-700"
          >
            {t('Close Date')}
          </label>
          <input
            type="datetime-local"
            name="closeDate"
            id="closeDate"
            className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.closeDate}
          />
          <ErrorMessage errors={errors.closeDate} touched={touched.closeDate} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 mt-8">
        <hr className="col-span-2" />

        <SlideShow
          onLastSlideRightArrowClick={handleLastSlideRightArrowClick}
          onDeleteSlide={handleDeleteSlide}
          current={currentSlide}
          setCurrent={setCurrentSlide}
          arrowPosition="top"
          contents={values.targets.map((_, index) => {
            const targetAudienceSelectorPopovers =
              getTargetAudienceSelectorPopovers(
                [
                  AUDIENCE_LABELS.invite,
                  AUDIENCE_LABELS.responses,
                  AUDIENCE_LABELS.textResponses,
                ],
                index
              );
            return (
              <div className="flex flex-col space-y-6">
                <div className="col-span-2">
                  <Toggle
                    label={
                      <div>
                        <span className="font-medium text-gray-900">
                          {t('Send Email Immediately')}
                        </span>{' '}
                        <span className="text-gray-500">
                          {t('send-an-email-link')}
                        </span>
                        {isSelectedTemplatePasscodeRequired && (
                          <>
                            <span className="font-medium text-blue-500">
                              (recommended as this form requires a passcode{' '}
                              <i className="text-blue-500 fa-solid fa-key" />)
                            </span>
                            <span className="font-medium text-red-500">
                              <strong> Note:</strong> Sending a new email will
                              invalidate any previous passcode for this form
                            </span>
                          </>
                        )}
                      </div>
                    }
                    enabled={values.targets[index].sendEmail || false}
                    setEnabled={(isEnabled) => {
                      setFieldValue(`targets[${index}].sendEmail`, isEnabled);
                    }}
                  />
                </div>
                <div className="col-span-2 grid grid-cols-1 gap-6">
                  <div>
                    <div className="font-bold">
                      Add target audience for form recipients
                    </div>
                    {targetAudienceSelectorPopovers[0]}
                    <TargetReach
                      target={audienceTargets[index]}
                      namesAnyone={targetNamesAnyone(index)}
                      count={audienceCounts?.perTarget[index]}
                      unreachable={audienceCounts?.unreachablePerTarget[index]}
                      error={targetLevelError(index)}
                    />
                  </div>

                  <hr className="col-span-2" />

                  <div className="col-span-2 flex space-x-2">
                    <input
                      id={`targets[${index}].shouldSendConfirmationEmailToFormSubmitter`}
                      type="checkbox"
                      checked={
                        values.targets[index]
                          .shouldSendConfirmationEmailToFormSubmitter
                      }
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <label
                      htmlFor="shouldSendConfirmationEmailToFormSubmitter"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t('Send Confirmation Email to Form Submitter')}
                    </label>
                  </div>

                  <div className="col-span-2">
                    <Toggle
                      label={
                        <div>
                          <span className="font-medium text-gray-900">
                            {t('Send Email Responses')}
                          </span>{' '}
                          <span className="text-gray-500">
                            {t('immediately-send-email-responses')}
                          </span>
                        </div>
                      }
                      enabled={
                        values.targets[index].addResponseEmailRecipients ||
                        false
                      }
                      setEnabled={(isEnabled) => {
                        setFieldValue(
                          `targets[${index}].addResponseEmailRecipients`,
                          isEnabled
                        );
                      }}
                    />
                  </div>
                  {values.targets[index].addResponseEmailRecipients ? (
                    <div>
                      <div className="font-bold">
                        Add target audience for email responses
                      </div>
                      {targetAudienceSelectorPopovers[1]}
                      <TargetReach
                        target={responseTargets[index]}
                        namesAnyone={namesAnyone(responseTargets[index])}
                        count={responseCounts?.perTarget[index]}
                        unreachable={responseCounts?.unreachablePerTarget[index]}
                      />
                    </div>
                  ) : null}

                  <hr className="col-span-2" />

                  <div className="col-span-2 flex space-x-2">
                    <input
                      id={`targets[${index}].shouldSendConfirmationSmsToFormSubmitter`}
                      type="checkbox"
                      checked={
                        values.targets[index]
                          .shouldSendConfirmationSmsToFormSubmitter
                      }
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <label
                      htmlFor="shouldSendConfirmationSmsToFormSubmitter"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t('Send Confirmation Text to Form Submitter')}
                    </label>
                  </div>

                  <div className="col-span-2">
                    <Toggle
                      label={
                        <div>
                          <span className="font-medium text-gray-900">
                            {t('Send Text Responses')}
                          </span>{' '}
                          <span className="text-gray-500">
                            {t('immediately-send-sms-responses')}
                          </span>
                        </div>
                      }
                      enabled={
                        values.targets[index].addResponseTextRecipients || false
                      }
                      setEnabled={(isEnabled) => {
                        setFieldValue(
                          `targets[${index}].addResponseTextRecipients`,
                          isEnabled
                        );
                      }}
                    />
                  </div>
                  {values.targets[index].addResponseTextRecipients ? (
                    <div>
                      <div className="font-bold">
                        Add target audience for text responses
                      </div>
                      {targetAudienceSelectorPopovers[2]}
                      <TargetReach
                        target={textResponseTargets[index]}
                        namesAnyone={namesAnyone(textResponseTargets[index])}
                        count={textResponseCounts?.perTarget[index]}
                        channel="text"
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        />
      </div>

      {/* A blocked Save needs to say so: the button disables itself when the
          form is invalid, and some of those fields sit on other slides. */}
      {Object.keys(errors).length > 0 && (
        <p className="text-sm text-red-500" role="alert">
          {t('form-has-errors')}
        </p>
      )}
      {audienceProblem && (
        <p className="text-sm text-red-500" role="alert">
          {audienceProblem}
        </p>
      )}
      {submitError && (
        <p className="text-sm text-red-500" role="alert">
          {submitError}
        </p>
      )}

      {/* Sticky rather than absolute.

          `absolute bottom-0` took the bar out of the flow, so nothing reserved
          space for it and it sat on top of whatever the form ended with —
          which, once a target had text responses enabled, was the audience
          selector for them. Sticky keeps the bar pinned to the bottom of the
          scrolling panel and still occupies its own space, so the content
          above it can always be reached.

          No z-index on purpose: being positioned is enough to paint above the
          form's static content, and the audience popovers are `fixed z-10`
          further up the DOM — matching that here would put this bar over them,
          since equal z-index is decided by document order. */}
      <div className="sticky bottom-0 -mx-4 mt-6 flex justify-end space-x-2 border-t border-gray-200 bg-white px-4 py-4 sm:-mx-6 sm:px-6">
        <Button
          type="button"
          variant="danger"
          onClick={() => {
            // A form this dialog already created stays created; the list
            // needs to show it.
            if (savedForm) {
              refetchForms?.();
            }
            resetForm();
            setSavedForm(null);
            setOpen(false);
          }}
        >
          {t('Cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={Object.keys(errors).length > 0 || !!audienceProblem}
        >
          {t('Save')}
        </Button>
      </div>
    </form>
  );
}

export default AddFormForm;
